import React from 'react';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import PriceHistoryChart from '../PriceHistoryChart';
import { Loader2, RefreshCw, Square, ExternalLink, Sparkles } from 'lucide-react';

export default function AlertSidebar({
  alert, summary, estimatedHistory, scrapedListings, loadingEstimates,
  refreshing, handleRefresh, handleStopScraping
}) {
  return (
    <div className="space-y-6">
      {/* Price History */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Price History</h3>
        {loadingEstimates ? (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-3 animate-spin" />
            <p className="text-gray-600 font-medium">Generating AI price estimates...</p>
            <p className="text-gray-500 text-sm mt-1">Analyzing market trends for your search</p>
          </div>
        ) : (scrapedListings.length > 0 || estimatedHistory.length > 0) ? (
          <>
            {estimatedHistory.length > 0 && scrapedListings.length === 0 && (
              <div className="mb-3 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                <span>Showing AI-estimated prices based on market data. Real data will appear as scraping progresses.</span>
              </div>
            )}
            <PriceHistoryChart scrapedListings={scrapedListings} estimatedHistory={estimatedHistory} />
          </>
        ) : alert?.status === 'running' ? (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 text-gray-400 mx-auto mb-3 animate-spin" />
            <p className="text-gray-600 font-medium">Waiting for scraping to start...</p>
            <p className="text-gray-500 text-sm mt-1">Price data will appear shortly</p>
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg"><p className="text-gray-500">No price data available</p></div>
        )}

        {alert?.global_circulation && (
          <div className="mt-6 bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-blue-600" /><span className="text-sm font-semibold text-blue-900">Estimated Circulation</span></div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-blue-600">{alert.global_circulation.active_range}</span>
              <span className="text-sm text-blue-700">units estimated</span>
            </div>
            <div className="mt-3 pt-3 border-t border-blue-200 space-y-1.5">
              {alert.global_circulation.category && <div className="flex items-center gap-2 text-xs text-blue-800"><span className="font-medium w-20 shrink-0">Category:</span><span>{alert.global_circulation.category}</span></div>}
              {alert.global_circulation.listing_count > 0 && <div className="flex items-center gap-2 text-xs text-blue-800"><span className="font-medium w-20 shrink-0">Listings:</span><span>{alert.global_circulation.listing_count} active on marketplace</span></div>}
              {alert.global_circulation.method && <div className="flex items-center gap-2 text-xs text-blue-800"><span className="font-medium w-20 shrink-0">Method:</span><span>{alert.global_circulation.method === 'marketplace_inference' ? `${alert.global_circulation.listing_count} listings x category multiplier` : 'Category baseline (no listings yet)'}</span></div>}
              <div className="flex items-center gap-2 text-xs text-blue-800"><span className="font-medium w-20 shrink-0">Source:</span><span>{alert.global_circulation.source}</span></div>
              <div className="flex items-center gap-2 text-xs text-blue-800"><span className="font-medium w-20 shrink-0">Confidence:</span><span>{alert.global_circulation.confidence === 'high' ? 'High' : alert.global_circulation.confidence === 'medium' ? 'Medium' : 'Low'}{alert.global_circulation.confidence === 'low' && ' — estimate will improve as more listings are scraped'}</span></div>
            </div>
            <p className="text-[10px] text-blue-500 mt-2 italic">Inferred from marketplace activity and category benchmarks. Not an official figure.</p>
          </div>
        )}
      </div>

      {/* Alert Info */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Alert Info</h3>
        <div className="space-y-3">
          <div><p className="text-xs text-gray-500 mb-1">Search Query</p><p className="font-semibold text-gray-900">{alert.query || 'Custom URL'}</p></div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Marketplaces</p>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {(alert.marketplaces || ['carousell', 'ebay']).map(m => (
                <span key={m} className={`text-xs px-2 py-0.5 rounded-full font-medium ${m === 'carousell' ? 'bg-red-100 text-red-700' : m === 'ebay' ? 'bg-blue-100 text-blue-700' : m === 'shopee' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700'}`}>
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </span>
              ))}
            </div>
          </div>
          <div><p className="text-xs text-gray-500 mb-1">Alert ID</p><p className="text-xs font-mono text-gray-700 bg-gray-50 px-2 py-1 rounded">{alert._id || alert.id}</p></div>
          <div><p className="text-xs text-gray-500 mb-1">Total Results</p><p className="text-2xl font-bold text-gray-900">{summary.totalResults}</p></div>
          <Separator />
          <div>
            <p className="text-xs text-gray-500 mb-1">Status</p>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${alert.status === 'active' ? 'bg-green-100 text-green-800' : alert.status === 'running' ? 'bg-blue-100 text-blue-800' : alert.status === 'error' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>{alert.status}</span>
          </div>
          {alert.last_run && <div><p className="text-xs text-gray-500 mb-1">Last Updated</p><p className="text-sm text-gray-900">{new Date(alert.last_run).toLocaleString()}</p></div>}
          {alert.last_error && alert.last_error.includes('Retrying') && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-center gap-2"><Loader2 className="w-4 h-4 text-yellow-600 animate-spin" /><span className="text-sm text-yellow-800 font-medium">Retry Scheduled</span></div>
              <p className="text-xs text-yellow-700 mt-1">{alert.last_error}</p>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <Button onClick={handleRefresh} disabled={refreshing} className="w-full bg-red-500 hover:bg-red-600 text-white">
          {refreshing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Refreshing...</> : <><RefreshCw className="w-4 h-4 mr-2" />Refresh Results</>}
        </Button>
        {alert.status === 'running' && (
          <Button onClick={handleStopScraping} variant="destructive" className="w-full" disabled={refreshing}>
            <Square className="w-4 h-4 mr-2" />Stop Scraping
          </Button>
        )}
        {alert.url && (
          <Button onClick={() => window.open(alert.url, '_blank')} variant="outline" className="w-full">
            <ExternalLink className="w-4 h-4 mr-2" />View on Carousell
          </Button>
        )}
      </div>
    </div>
  );
}
